import { useQuery } from '@tanstack/react-query'
import type { SendChannel } from '@/services/notificationService'
import { getTemplateVariables } from '@/services/notificationService'
import {
  findMalformedPlaceholders,
  findUnknownTokens,
} from '@/utils/templateTokens'
import { channelKind, charLimit, sortForPanels } from './channelKinds'
import { ChannelDrafts, EMPTY_DRAFT } from './sendContent'

export type ComposeChecks = {
  unknown: string[]
  malformed: string[]
  overLimit: boolean
  everyTickedHasText: boolean
}

/** What the send rules say about the ticked panels. The window and the alert node share them. */
export const composeChecks = (
  channels: SendChannel[],
  tickedIds: number[],
  drafts: ChannelDrafts,
  allowedVariables: string[]
): ComposeChecks => {
  const ticked = sortForPanels(channels).filter(c =>
    tickedIds.includes(c.channelId)
  )
  const draftOf = (c: SendChannel) => drafts[c.channelId] ?? EMPTY_DRAFT
  const texts = ticked.flatMap(c => [draftOf(c).body, draftOf(c).subject])
  return {
    unknown: [
      ...new Set(texts.flatMap(t => findUnknownTokens(t, allowedVariables))),
    ],
    // Malformed fragments ({{ name }}, {{כתובת}}, unclosed {{) never render:
    // distinct copy from the unknown-variable warning, and they block the send.
    malformed: [...new Set(texts.flatMap(findMalformedPlaceholders))],
    overLimit: ticked.some(
      c => draftOf(c).body.length > (charLimit(channelKind(c)) ?? Infinity)
    ),
    everyTickedHasText: ticked.every(c => draftOf(c).body.trim() !== ''),
  }
}

export const useComposeChecks = (
  accountId: number,
  enabled: boolean,
  channels: SendChannel[],
  tickedIds: number[],
  drafts: ChannelDrafts
): ComposeChecks => {
  const { data: variables = [] } = useQuery({
    queryKey: ['template-variables', accountId],
    queryFn: () => getTemplateVariables(accountId),
    enabled,
  })
  return composeChecks(
    channels,
    tickedIds,
    drafts,
    variables.map(v => v.variable)
  )
}
